import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { withRequestDeadline } from "../services/requestDeadline";
import { remoteRepository } from "../services/remoteRepository";
import { supabase, hasSupabaseConfig } from "../services/supabaseClient";
import type {
  CalendarEvent,
  AppNotification,
  Credential,
  DemoState,
  Enrollment,
  EnrollmentAnswersInput,
  EnrollmentBatch,
  Institution,
  LoginMode,
  Material,
  Olympiad,
  Profile,
  ResultRecord,
  ServiceResult,
  Student,
  Teacher,
} from "./types";

interface ToastMessage {
  id: string;
  type: "success" | "error" | "info";
  title: string;
  description?: string;
}

interface AppContextValue {
  profile: Profile | null;
  authLoading: boolean;
  authProgress: number;
  bootstrapError: string | null;
  retryBootstrap: () => void;
  data: DemoState | null;
  dataLoading: boolean;
  dataError: string | null;
  toasts: ToastMessage[];
  login: (
    identity: string,
    password: string,
    mode: LoginMode,
    keepConnected?: boolean,
  ) => Promise<ServiceResult<Profile>>;
  logout: () => void;
  changeActiveUnit: (unitId: string) => Promise<void>;
  requestRecovery: (
    identity: string,
    mode: LoginMode,
  ) => Promise<ServiceResult<{ message: string }>>;
  refresh: () => Promise<void>;
  approveEnrollment: (id: string) => Promise<ServiceResult<Enrollment>>;
  updateEnrollmentPayment: (
    enrollmentId: string,
    paymentId: string,
    status: "paid" | "pending" | "invalidated",
  ) => Promise<ServiceResult<Enrollment>>;
  cancelEnrollment: (id: string) => Promise<ServiceResult<Enrollment>>;
  createEnrollment: (
    olympiadId: string,
    teamName?: string,
    teamMemberIds?: string[],
    answers?: EnrollmentAnswersInput,
    requestId?: string,
  ) => Promise<ServiceResult<Enrollment>>;
  createEnrollmentForStudents: (
    olympiadId: string,
    studentIds: string[],
    teamName?: string,
  ) => Promise<ServiceResult<EnrollmentBatch>>;
  saveOlympiad: (olympiad: Olympiad) => Promise<ServiceResult<Olympiad>>;
  createMaterial: (material: Omit<Material, "id" | "createdAt" | "isNew" | "filePath" | "fileName" | "mimeType">, file: File) => Promise<ServiceResult<Material>>;
  openMaterial: (material: Material) => ReturnType<typeof remoteRepository.openMaterial>;
  setOlympiadPublished: (olympiadId: string, published: boolean) => Promise<ServiceResult<Olympiad>>;
  saveResult: (result: ResultRecord) => Promise<ServiceResult<ResultRecord>>;
  setResultsPublished: (resultIds: string[], published: boolean) => Promise<ServiceResult<number>>;
  qualifyForNextPhase: (resultId: string) => ReturnType<typeof remoteRepository.qualifyForNextPhase>;
  updateStudent: (student: Student) => Promise<ServiceResult<Student>>;
  createStudent: (
    student: Omit<Student, "id" | "initials" | "lastAccess">,
  ) => Promise<ServiceResult<Student>>;
  setStudentActive: (
    studentId: string,
    active: boolean,
  ) => Promise<ServiceResult<Student>>;
  updateTeacherPermissions: (
    teacherId: string,
    permissions: string[],
    unitIds: string[],
  ) => Promise<ServiceResult<Teacher>>;
  setTeacherStatus: (teacherId: string, status: Teacher["status"]) => Promise<ServiceResult<Teacher>>;
  createTeacher: (teacher: Omit<Teacher, "id" | "initials" | "lastAccess">) => Promise<ServiceResult<Teacher>>;
  createNotification: (notification: Omit<AppNotification, "id" | "createdAt" | "read">) => Promise<ServiceResult<AppNotification>>;
  createCredential: (credential: Omit<Credential, "id" | "updatedAt" | "allowedRoles">) => Promise<ServiceResult<Credential>>;
  updateCredential: (id: string, credential: Omit<Credential, "id" | "updatedAt" | "allowedRoles">) => Promise<ServiceResult<Credential>>;
  createCalendarEvent: (
    event: Omit<CalendarEvent, "id" | "type">,
  ) => Promise<ServiceResult<CalendarEvent>>;
  saveInstitution: (
    institution: Institution,
  ) => Promise<ServiceResult<Institution>>;
  markAllNotificationsRead: () => Promise<ServiceResult<number>>;
  resetStudentPassword: (
    id: string,
    requestId?: string,
  ) => ReturnType<typeof remoteRepository.resetStudentPassword>;
  resetTeacherPassword: (id: string, requestId?: string) => ReturnType<typeof remoteRepository.resetTeacherPassword>;
  endUserSessions: (id: string) => ReturnType<typeof remoteRepository.endUserSessions>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<ServiceResult<Profile>>;
  updateOwnProfile: (name: string, shortName: string, contactEmail?: string, cpf?: string) => Promise<ServiceResult<Profile>>;
  revealCredential: (
    id: string,
  ) => ReturnType<typeof remoteRepository.revealCredential>;
  notify: (message: Omit<ToastMessage, "id">) => void;
  dismissToast: (id: string) => void;
}

import { millisecondsToNextSchoolDay, olympiadStatus } from './schoolTime';

const AppContext = createContext<AppContextValue | null>(null);
const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000;
const canManageOlympiads = (permissions: string[]) => permissions.includes('*') || permissions.includes('olympiads:manage') || permissions.includes('olympiads:create') || permissions.includes('olympiads:publish');

export function AppProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authProgress, setAuthProgress] = useState(0);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);
  const retryBootstrap = useCallback(() => setBootstrapAttempt((attempt) => attempt + 1), []);
  const [data, setData] = useState<DemoState | null>(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const refreshRevision = useRef(0);
  // Unlike refreshes, leaving and returning to the same unit creates a new
  // context for writes that may still be awaiting a server response.
  const contextRevision = useRef(0);
  const pendingLogin = useRef<{ revision: number; awaitingCleanup: boolean } | null>(null);
  const pendingRefresh = useRef<{ revision: number; promise: Promise<void> } | null>(null);
  const activeUnitRef = useRef(profile?.unitId);
  activeUnitRef.current = profile?.unitId;
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    let timer: number;
    const update = () => {
      window.clearTimeout(timer);
      const now = new Date(); setClock(now);
      timer = window.setTimeout(update, millisecondsToNextSchoolDay(now));
    };
    update();
    window.addEventListener('focus', update);
    const onVisibility = () => { if (document.visibilityState === 'visible') { window.clearTimeout(timer); update(); } };
    document.addEventListener('visibilitychange', onVisibility);
    const { data: subscription } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' && pendingLogin.current) pendingLogin.current.awaitingCleanup = false;
      if (event === 'SIGNED_OUT') {
        ++contextRevision.current;
        const revision = ++refreshRevision.current;
        // Repository.login first clears the local session. Only that initial
        // event belongs to this attempt; later sign-outs invalidate it.
        if (pendingLogin.current?.awaitingCleanup) {
          pendingLogin.current.awaitingCleanup = false;
          pendingLogin.current.revision = revision;
        } else pendingLogin.current = null;
        activeUnitRef.current = undefined;
        pendingRefresh.current = null;
        setProfile(null); setData(null); setDataError(null); setBootstrapError(null); setDataLoading(false);
        setAuthLoading(false);
      }
    });
    return () => { window.clearTimeout(timer); window.removeEventListener('focus', update); document.removeEventListener('visibilitychange', onVisibility); subscription.subscription.unsubscribe(); };
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((current) => current.filter((item) => item.id !== id));
  }, []);

  const notify = useCallback(
    (message: Omit<ToastMessage, "id">) => {
      const id = crypto.randomUUID();
      setToasts((current) => [...current, { id, ...message }]);
      window.setTimeout(() => dismissToast(id), 4000);
    },
    [dismissToast],
  );

  // Every data load shares the same revision, including restoration and login.
  // A completion from a previous session or unit must never update this screen.
  const loadData = useCallback(async (viewer: Profile, revision: number, signal?: AbortSignal) => {
    try {
      const result = await withRequestDeadline((requestSignal) => remoteRepository.getState(viewer.unitId, viewer, requestSignal), signal);
      if (revision !== refreshRevision.current) return;
      if (result.ok) setData(result.data);
      setDataError(result.ok ? null : result.error.message);
    } catch {
      if (revision === refreshRevision.current) {
        setDataError('Não foi possível concluir a operação. Tente novamente.');
      }
    } finally {
      if (revision === refreshRevision.current) setDataLoading(false);
    }
  }, []);

  const refresh = useCallback((silent = false): Promise<void> => {
    if (!profile || profile.unitId !== activeUnitRef.current) return Promise.resolve();
    if (!silent && pendingRefresh.current?.revision === refreshRevision.current) return pendingRefresh.current.promise;
    const revision = ++refreshRevision.current;
    if (!silent) setDataLoading(true);
    const attempt = loadData(profile, revision);
    if (!silent) {
      pendingRefresh.current = { revision, promise: attempt };
      void attempt.finally(() => {
        if (pendingRefresh.current?.promise === attempt) pendingRefresh.current = null;
      });
    }
    return attempt;
  }, [loadData, profile]);

  useEffect(() => {
    const revision = ++refreshRevision.current;
    const controller = new AbortController();
    const restore = async () => {
      setAuthLoading(true);
      setBootstrapError(null);
      try {
        if (!hasSupabaseConfig) return;
        setAuthProgress(12);
        await withRequestDeadline(async (signal) => {
          const storedProfile = await remoteRepository.getCurrentProfile(signal);
          if (revision !== refreshRevision.current || signal.aborted) return;
          if (!storedProfile.ok) {
            if (storedProfile.error.code !== 'FORBIDDEN') throw new Error(storedProfile.error.message);
            activeUnitRef.current = undefined;
            setProfile(null);
            setData(null);
            setDataError(null);
            return;
          }
          setAuthProgress(46);
          activeUnitRef.current = storedProfile.data.unitId;
          setProfile(storedProfile.data);
          if (!storedProfile.data.mustChangePassword) {
            setDataLoading(true);
            await loadData(storedProfile.data, revision, signal);
          }
          if (revision === refreshRevision.current && !signal.aborted) setAuthProgress(92);
        }, controller.signal);
      } catch {
        if (revision === refreshRevision.current) {
          setBootstrapError('Não foi possível concluir o carregamento. Verifique sua conexão e tente novamente.');
        }
      } finally {
        if (revision === refreshRevision.current) {
          setDataLoading(false);
          setAuthProgress(100);
          setAuthLoading(false);
        }
      }
    };
    void restore();
    return () => { controller.abort(); ++contextRevision.current; ++refreshRevision.current; pendingLogin.current = null; };
  }, [loadData, bootstrapAttempt]);

  const login = useCallback(
    async (identity: string, password: string, mode: LoginMode, keepConnected = false) => {
      ++contextRevision.current;
      const attempt = { revision: ++refreshRevision.current, awaitingCleanup: true };
      pendingLogin.current = attempt;
      pendingRefresh.current = null;
      try {
        const result = await remoteRepository.login(identity, password, mode, keepConnected);
        if (pendingLogin.current !== attempt || attempt.revision !== refreshRevision.current) return result;
        pendingLogin.current = null;
        if (result.ok) {
          activeUnitRef.current = result.data.unitId;
          setProfile(result.data);
          setData(null);
          setDataError(null);
          setDataLoading(true);
          await loadData(result.data, attempt.revision);
        }
        return result;
      } finally {
        if (pendingLogin.current === attempt) pendingLogin.current = null;
      }
    },
    [loadData],
  );

  const logout = useCallback(() => {
    ++contextRevision.current;
    pendingLogin.current = null;
    ++refreshRevision.current;
    activeUnitRef.current = undefined;
    pendingRefresh.current = null;
    void remoteRepository.logout();
    setProfile(null);
    setData(null);
    setDataError(null);
    setBootstrapError(null);
    setDataLoading(false);
    setAuthLoading(false);
  }, []);

  useEffect(() => {
    if (!profile) return;
    let timer = window.setTimeout(logout, INACTIVITY_TIMEOUT_MS);
    const registerActivity = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(logout, INACTIVITY_TIMEOUT_MS);
    };
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((event) => window.addEventListener(event, registerActivity, { passive: true }));
    return () => {
      window.clearTimeout(timer);
      events.forEach((event) => window.removeEventListener(event, registerActivity));
    };
  }, [logout, profile]);

  const afterMutation = useCallback(
    async <T,>(
      operation: () => Promise<ServiceResult<T>>,
      reconcile?: (state: DemoState, value: T) => DemoState,
    ) => {
      const contextAtStart = contextRevision.current;
      const result = await operation();
      if (contextRevision.current !== contextAtStart) return result;
      if (result.ok) {
        if (reconcile)
          setData((current) => current && contextRevision.current === contextAtStart ? reconcile(current, result.data) : current);
        void refresh(true);
      }
      else if (result.error.retryable) await refresh(true);
      return result;
    },
    [refresh],
  );

  const value = useMemo<AppContextValue>(
    () => ({
      profile,
      authLoading,
      authProgress,
      bootstrapError,
      retryBootstrap,
      data: data ? { ...data, olympiads: data.olympiads.map((item) => ({ ...item, status: olympiadStatus(item, clock) })) } : null,
      dataLoading,
      dataError,
      toasts,
      login,
      logout,
      changeActiveUnit: async (unitId) => {
        if (!profile || !(profile.unitIds ?? [profile.unitId]).includes(unitId)) return;
        ++contextRevision.current;
        remoteRepository.setActiveUnit(unitId);
        const next = { ...profile, unitId };
        const revision = ++refreshRevision.current;
        pendingRefresh.current = null;
        activeUnitRef.current = unitId;
        setProfile(next);
        setData(null);
        setDataError(null);
        setDataLoading(true);
        await loadData(next, revision);
      },
      requestRecovery: (identity, mode) =>
        remoteRepository.requestPasswordRecovery(identity, mode),
      refresh,
      approveEnrollment: (id) =>
        profile && (profile.permissions.includes("*") || profile.permissions.includes("payments:manage")) ? afterMutation(() => remoteRepository.approveEnrollment(id), (state, updated) => ({ ...state, enrollments: state.enrollments.map((item) => item.id === id ? updated : item) })) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Seu perfil não pode aprovar inscrições pagas.", retryable: false } }),
      updateEnrollmentPayment: (enrollmentId, paymentId, status) =>
        profile && (profile.permissions.includes("*") || profile.permissions.includes("payments:manage")) ? afterMutation(() =>
          remoteRepository.updateEnrollmentPayment(
            enrollmentId,
            paymentId,
            status,
          ), (state, updated) => ({ ...state, enrollments: state.enrollments.map((item) => item.id === updated.id ? updated : item) })
        ) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Seu perfil não pode registrar pagamentos.", retryable: false } }),
      cancelEnrollment: (id) =>
        profile && (profile.permissions.includes("*") || profile.permissions.includes("enrollments:create")) ? afterMutation(() => remoteRepository.cancelEnrollment(id), (state, updated) => ({ ...state, enrollments: state.enrollments.map((item) => item.id === id ? updated : item) })) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Seu perfil não pode cancelar inscrições.", retryable: false } }),
      createEnrollment: (olympiadId, teamName, teamMemberIds, answers, requestId) => {
        if (!profile)
          return Promise.resolve({
            ok: false,
            error: {
              code: "FORBIDDEN",
              message: "Entre para solicitar uma inscrição.",
              retryable: false,
            },
          });
        return afterMutation(
          () => remoteRepository.createEnrollment(olympiadId, profile.id, teamName, teamMemberIds, "student", answers, requestId),
          (state, created) => ({ ...state, enrollments: [...state.enrollments.filter((item) => item.id !== created.id), created] }),
        );
      },
      createEnrollmentForStudents: async (olympiadId, studentIds, teamName) => {
        if (!profile || profile.role === "student" || (!profile.permissions.includes("*") && !profile.permissions.includes("enrollments:create")))
          return {
            ok: false,
            error: { code: "FORBIDDEN", message: "Ação restrita a professores.", retryable: false },
          };
        const olympiad = data?.olympiads.find((item) => item.id === olympiadId);
        if (!olympiad || !studentIds.length)
          return {
            ok: false,
            error: { code: "VALIDATION", message: "Selecione a olimpíada e ao menos um estudante.", retryable: false },
          };
        const contextAtStart = contextRevision.current;
        const created: Enrollment[] = [];
        const failures: EnrollmentBatch['failures'] = [];
        const groups = olympiad.format === 'Equipe' ? [studentIds] : [...new Set(studentIds)].map((id) => [id]);
        for (const [leaderId, ...memberIds] of groups) {
          if (contextRevision.current !== contextAtStart) {
            [leaderId, ...memberIds].forEach((studentId) => failures.push({ studentId, message: 'A sessão ou unidade mudou. Confira as inscrições antes de tentar novamente.' }));
            continue;
          }
          const result = await remoteRepository.createEnrollment(olympiadId, leaderId, teamName, memberIds, 'teacher');
          if (result.ok) created.push(result.data);
          else groups.length === 1 && olympiad.format === 'Equipe'
            ? studentIds.forEach((studentId) => failures.push({ studentId, message: result.error.message }))
            : failures.push({ studentId: leaderId, message: result.error.message });
        }
        if (contextRevision.current !== contextAtStart) return { ok: true, data: { created, failures } };
        // Reconcile even after a timeout: the server may have committed while the response was lost.
        const revision = refreshRevision.current;
        const state = await remoteRepository.getState(profile.unitId, profile);
        if (state.ok) {
          for (let i = failures.length - 1; i >= 0; i--) {
            const existing = state.data.enrollments.find((item) => item.olympiadId === olympiadId && item.status !== 'cancelled' && item.studentIds.includes(failures[i].studentId));
            if (existing) { if (!created.some((item) => item.id === existing.id)) created.push(existing); failures.splice(i, 1); }
          }
          if (contextRevision.current === contextAtStart && refreshRevision.current === revision) setData(state.data);
        } else if (contextRevision.current === contextAtStart && refreshRevision.current === revision) {
          setData((current) => current ? { ...current, enrollments: [...current.enrollments.filter((item) => !created.some((entry) => entry.id === item.id)), ...created] } : current);
        }
        return { ok: true, data: { created, failures } };
      },
      saveOlympiad: (olympiad) =>
        profile && canManageOlympiads(profile.permissions) ? afterMutation(() => remoteRepository.saveOlympiad({ ...olympiad, unitId: olympiad.unitId || profile.unitId }), (state, saved) => ({ ...state, olympiads: state.olympiads.some((item) => item.id === saved.id) ? state.olympiads.map((item) => item.id === saved.id ? saved : item) : [...state.olympiads, saved] })) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Seu perfil não pode gerenciar olimpíadas.", retryable: false } }),
      createMaterial: (material, file) =>
        profile && (profile.permissions.includes("*") || profile.permissions.includes("materials:manage")) ? afterMutation(() => remoteRepository.createMaterial(material, file), (state, created) => ({ ...state, materials: [created, ...state.materials] })) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Seu perfil não pode cadastrar materiais.", retryable: false } }),
      openMaterial: (material) => remoteRepository.openMaterial(material),
      setOlympiadPublished: (olympiadId, published) =>
        profile && canManageOlympiads(profile.permissions) ? afterMutation(() => remoteRepository.setOlympiadPublished(olympiadId, published), (state, updated) => ({ ...state, olympiads: state.olympiads.map((item) => item.id === olympiadId ? updated : item) })) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Seu perfil não pode gerenciar olimpíadas.", retryable: false } }),
      saveResult: (result) =>
        profile && (profile.permissions.includes("*") || profile.permissions.includes("results:manage")) ? afterMutation(() => remoteRepository.saveResult(result), (state, saved) => ({ ...state, results: state.results.some((item) => item.id === saved.id) ? state.results.map((item) => item.id === saved.id ? saved : item) : [...state.results, saved] })) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Seu perfil não pode lançar resultados.", retryable: false } }),
      setResultsPublished: (resultIds, published) =>
        profile && (profile.permissions.includes("*") || profile.permissions.includes("results:manage")) ? afterMutation(() => remoteRepository.setResultsPublished(resultIds, published), (state) => ({ ...state, results: state.results.map((item) => resultIds.includes(item.id) ? { ...item, published } : item) })) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Seu perfil não pode publicar resultados.", retryable: false } }),
      qualifyForNextPhase: (resultId) =>
        profile && (profile.permissions.includes("*") || profile.permissions.includes("results:manage")) ? afterMutation(() => remoteRepository.qualifyForNextPhase(resultId), (state, updated) => ({ ...state, results: state.results.map((item) => item.id === updated.result.id ? updated.result : item), enrollments: state.enrollments.map((item) => item.id === updated.enrollment.id ? updated.enrollment : item) })) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Seu perfil não pode alterar classificações.", retryable: false } }),
      updateStudent: (student) =>
        profile && (profile.isSystemManager || profile.permissions.includes('students:manage')) ? afterMutation(() => remoteRepository.updateStudent(student), (state, updated) => ({ ...state, students: state.students.map((item) => item.id === updated.id ? updated : item) })) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Seu perfil não pode editar estudantes.", retryable: false } }),
      createStudent: (student) =>
        profile?.isSystemManager ? afterMutation(() => remoteRepository.createStudent(student), (state, created) => ({ ...state, students: [...state.students, created] })) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Ação exclusiva do Gestor de Sistema.", retryable: false } }),
      setStudentActive: (studentId, active) =>
        profile?.isSystemManager ? afterMutation(() =>
          remoteRepository.setStudentActive(studentId, active), (state) => ({ ...state, students: state.students.map((item) => item.id === studentId ? { ...item, active } : item) })
        ) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Ação exclusiva do Gestor de Sistema.", retryable: false } }),
      updateTeacherPermissions: (teacherId, permissions, unitIds) =>
        profile?.isSystemManager ? afterMutation(() =>
          remoteRepository.updateTeacherPermissions(teacherId, permissions, unitIds), (state) => ({ ...state, teachers: state.teachers.map((item) => item.id === teacherId ? { ...item, permissions, unitIds } : item) })
        ) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Ação exclusiva do Gestor de Sistema.", retryable: false } }),
      setTeacherStatus: (teacherId, status) =>
        profile?.isSystemManager ? afterMutation(() => remoteRepository.setTeacherStatus(teacherId, status), (state) => ({ ...state, teachers: state.teachers.map((item) => item.id === teacherId ? { ...item, status } : item) })) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Ação exclusiva do Gestor de Sistema.", retryable: false } }),
      createTeacher: (teacher) =>
        profile?.isSystemManager ? afterMutation(() => remoteRepository.createTeacher(teacher), (state, created) => ({ ...state, teachers: [...state.teachers, created] })) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Ação exclusiva do Gestor de Sistema.", retryable: false } }),
      createNotification: (notification) =>
        profile?.isSystemManager ? afterMutation(() => remoteRepository.createNotification(notification), (state, created) => ({ ...state, notifications: [created, ...state.notifications] })) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Somente o Gestor de Sistema pode enviar comunicados.", retryable: false } }),
      createCredential: (credential) =>
        profile?.isSystemManager ? afterMutation(() => remoteRepository.createCredential(credential), (state, created) => ({ ...state, credentials: [...state.credentials, created] })) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Ação exclusiva do Gestor de Sistema.", retryable: false } }),
      updateCredential: (id, credential) =>
        profile?.isSystemManager ? afterMutation(() => remoteRepository.updateCredential(id, credential), (state, updated) => ({ ...state, credentials: state.credentials.map((item) => item.id === id ? updated : item) })) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Ação exclusiva do Gestor de Sistema.", retryable: false } }),
      createCalendarEvent: (event) =>
        profile && (profile.isSystemManager || profile.permissions.includes('*') || profile.permissions.includes('calendar:manage')) ? afterMutation(() => remoteRepository.createCalendarEvent({ ...event, unitId: event.unitId || profile.unitId }), (state, created) => ({ ...state, events: [...state.events.filter((item) => item.id !== created.id), created] })) : Promise.resolve({ ok: false, error: { code: 'FORBIDDEN', message: 'Seu perfil não pode criar eventos.', retryable: false } }),
      saveInstitution: (institution) =>
        profile?.isSystemManager ? afterMutation(() => remoteRepository.saveInstitution(institution), (state, saved) => ({ ...state, institution: saved })) : Promise.resolve({ ok: false, error: { code: "FORBIDDEN", message: "Ação exclusiva do Gestor de Sistema.", retryable: false } }),
      markAllNotificationsRead: () =>
        afterMutation(() => remoteRepository.markAllNotificationsRead(), (state) => ({ ...state, notifications: state.notifications.map((item) => ({ ...item, read: true })) })),
      resetStudentPassword: async (id, requestId) => {
        if (!profile?.isSystemManager) return { ok: false, error: { code: "FORBIDDEN", message: "Ação exclusiva do Gestor de Sistema.", retryable: false } };
        const result = await remoteRepository.resetStudentPassword(id, requestId);
        if (result.ok) void refresh(true);
        return result;
      },
      resetTeacherPassword: async (id, requestId) => {
        if (!profile?.isSystemManager) return { ok: false, error: { code: "FORBIDDEN", message: "Ação exclusiva do Gestor de Sistema.", retryable: false } };
        const result = await remoteRepository.resetTeacherPassword(id, requestId);
        if (result.ok) void refresh(true);
        return result;
      },
      endUserSessions: async (id) => {
        if (!profile?.isSystemManager) return { ok: false, error: { code: "FORBIDDEN", message: "Ação exclusiva do Gestor de Sistema.", retryable: false } };
        return remoteRepository.endUserSessions(id);
      },
      changePassword: async (currentPassword, newPassword) => {
        if (!profile) return { ok: false, error: { code: 'FORBIDDEN', message: 'Sessão não encontrada.', retryable: false } };
        const result = await remoteRepository.changePassword(currentPassword, newPassword);
        if (result.ok) { setProfile(result.data); void refresh(true); }
        return result;
      },
      updateOwnProfile: async (name, shortName, contactEmail, cpf) => {
        if (!profile) return { ok: false, error: { code: 'FORBIDDEN', message: 'Sessão não encontrada.', retryable: false } };
        const result = await remoteRepository.updateOwnProfile(name, shortName, contactEmail, cpf);
        if (result.ok) { setProfile(result.data); void refresh(true); }
        return result;
      },
      revealCredential: async (id) => {
        if (!profile)
          return {
            ok: false,
            error: {
              code: "FORBIDDEN",
              message: "Sessão não encontrada.",
              retryable: false,
            },
          };
        return remoteRepository.revealCredential(id, profile.role);
      },
      notify,
      dismissToast,
    }),
    [clock,
      afterMutation,
      authLoading,
      authProgress,
      bootstrapError,
      retryBootstrap,
      data,
      dataLoading,
      dataError,
      dismissToast,
      login,
      loadData,
      logout,
      notify,
      profile,
      refresh,
      toasts,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const value = useContext(AppContext);
  if (!value) throw new Error("useApp deve ser usado dentro de AppProvider");
  return value;
}
